import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { FileText, Plus, Edit, Trash2, Copy, Eye, Search, Filter, CheckCircle, XCircle, AlertTriangle, Lock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scriptTypes = [
  { value: 'inbound', label: 'Inbound Calls' },
  { value: 'outbound', label: 'Outbound Calls' },
  { value: 'sales', label: 'Sales' },
  { value: 'support', label: 'Customer Support' },
  { value: 'appointment', label: 'Appointment Setting' },
  { value: 'follow_up', label: 'Follow-up' }
];

export default function CallCenterScripts() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [previewScript, setPreviewScript] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    script_type: 'inbound',
    content: '',
    opening: '',
    closing: '',
    objection_handlers: {},
    is_active: true
  });
  const [newObjection, setNewObjection] = useState({ key: '', value: '' });

  const fetchScripts = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('script_type', typeFilter);
      
      const response = await axios.get(`${API}/call-center/scripts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScripts(response.data.scripts || []);
    } catch (err) {
      console.error('Failed to fetch scripts:', err);
      toast.error('Failed to load scripts');
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    fetchScripts();
  }, [fetchScripts]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      script_type: 'inbound',
      content: '',
      opening: '',
      closing: '',
      objection_handlers: {},
      is_active: true
    });
    setEditingScript(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (script) => {
    setEditingScript(script);
    setFormData({
      name: script.name,
      description: script.description || '',
      script_type: script.script_type,
      content: script.content,
      opening: script.opening || '',
      closing: script.closing || '',
      objection_handlers: script.objection_handlers || {},
      is_active: script.is_active
    });
    setShowDialog(true);
  };

  const saveScript = async () => {
    if (!formData.name || !formData.content) {
      toast.error('Name and content are required');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      if (editingScript) {
        await axios.patch(`${API}/call-center/scripts/${editingScript.id}`, formData, { headers });
        toast.success('Script updated successfully');
      } else {
        await axios.post(`${API}/call-center/scripts`, formData, { headers });
        toast.success('Script created successfully');
      }

      setShowDialog(false);
      resetForm();
      fetchScripts();
    } catch (err) {
      console.error('Failed to save script:', err);
      toast.error('Failed to save script');
    }
  };

  const deleteScript = async (scriptId) => {
    if (!confirm('Are you sure you want to delete this script?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/call-center/scripts/${scriptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Script deleted');
      fetchScripts();
    } catch (err) {
      console.error('Failed to delete script:', err);
      toast.error('Failed to delete script');
    }
  };

  const duplicateScript = (script) => {
    setFormData({
      ...script,
      name: `${script.name} (Copy)`,
      objection_handlers: { ...script.objection_handlers }
    });
    setEditingScript(null);
    setShowDialog(true);
  };

  const addObjectionHandler = () => {
    if (!newObjection.key || !newObjection.value) return;
    setFormData({
      ...formData,
      objection_handlers: {
        ...formData.objection_handlers,
        [newObjection.key]: newObjection.value
      }
    });
    setNewObjection({ key: '', value: '' });
  };

  const removeObjectionHandler = (key) => {
    const handlers = { ...formData.objection_handlers };
    delete handlers[key];
    setFormData({ ...formData, objection_handlers: handlers });
  };

  const filteredScripts = scripts.filter(script => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!script.name.toLowerCase().includes(search) &&
          !script.description?.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  return (
    <Layout>
    <div className="p-6 space-y-6">
      {/* Phase 2 Preview Banner */}
      <Alert className="bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-800">
        <Sparkles className="h-5 w-5 text-purple-600" />
        <AlertDescription className="ml-2">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-semibold text-purple-800 dark:text-purple-300">Phase 2 Prep Mode</span>
              <span className="text-purple-700 dark:text-purple-400 ml-2">
                Build and organize your call scripts now. They'll be available in the Dialer when Active Mode is enabled.
              </span>
            </div>
            <Badge variant="outline" className="border-purple-500 text-purple-700 dark:text-purple-300">
              Ready for Phase 2
            </Badge>
          </div>
        </AlertDescription>
      </Alert>

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Script Builder</h1>
          <p className="text-muted-foreground">Create and manage call scripts for your team</p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          Create Script
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search scripts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {scriptTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Scripts Grid */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : filteredScripts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold">No Scripts Found</h3>
            <p className="text-muted-foreground mb-4">Create your first call script to get started</p>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Create Script
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredScripts.map(script => (
            <Card key={script.id} className="flex flex-col">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {script.name}
                      {script.is_active ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-400" />
                      )}
                    </CardTitle>
                    <CardDescription className="line-clamp-2">{script.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge variant="outline">
                    {scriptTypes.find(t => t.value === script.script_type)?.label || script.script_type}
                  </Badge>
                  {Object.keys(script.objection_handlers || {}).length > 0 && (
                    <Badge variant="secondary">
                      {Object.keys(script.objection_handlers).length} objections
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {script.content?.substring(0, 150)}...
                </p>
              </CardContent>
              <div className="p-4 pt-0 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setPreviewScript(script);
                    setShowPreview(true);
                  }}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEditDialog(script)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => duplicateScript(script)}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => deleteScript(script.id)}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingScript ? 'Edit Script' : 'Create New Script'}</DialogTitle>
            <DialogDescription>Build a call script with opening, content, and objection handlers</DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="mt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="content">Script Content</TabsTrigger>
              <TabsTrigger value="objections">Objection Handlers</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div>
                <Label>Script Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Initial Sales Call"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of when to use this script"
                  rows={2}
                />
              </div>
              <div>
                <Label>Script Type</Label>
                <Select
                  value={formData.script_type}
                  onValueChange={(v) => setFormData({ ...formData, script_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {scriptTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
              </div>
            </TabsContent>

            <TabsContent value="content" className="space-y-4 mt-4">
              <div>
                <Label>Opening Statement</Label>
                <Textarea
                  value={formData.opening}
                  onChange={(e) => setFormData({ ...formData, opening: e.target.value })}
                  placeholder="How to open the call..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Main Script Content *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Main talking points and script flow..."
                  rows={10}
                />
              </div>
              <div>
                <Label>Closing Statement</Label>
                <Textarea
                  value={formData.closing}
                  onChange={(e) => setFormData({ ...formData, closing: e.target.value })}
                  placeholder="How to end the call..."
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="objections" className="space-y-4 mt-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <Label className="text-sm">Add Objection Handler</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input
                    value={newObjection.key}
                    onChange={(e) => setNewObjection({ ...newObjection, key: e.target.value })}
                    placeholder="Objection (e.g., 'Too expensive')"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={newObjection.value}
                      onChange={(e) => setNewObjection({ ...newObjection, value: e.target.value })}
                      placeholder="Response"
                    />
                    <Button onClick={addObjectionHandler} size="icon">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {Object.entries(formData.objection_handlers).map(([key, value]) => (
                  <div key={key} className="flex items-start gap-2 p-3 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-sm">If: "{key}"</p>
                      <p className="text-sm text-muted-foreground mt-1">{value}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeObjectionHandler(key)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                {Object.keys(formData.objection_handlers).length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No objection handlers added</p>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
            <Button onClick={saveScript}>{editingScript ? 'Save Changes' : 'Create Script'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewScript?.name}</DialogTitle>
            <DialogDescription>{previewScript?.description}</DialogDescription>
          </DialogHeader>
          {previewScript && (
            <div className="space-y-4 mt-4">
              {previewScript.opening && (
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-1">OPENING</h4>
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded border-l-4 border-green-500">
                    {previewScript.opening}
                  </div>
                </div>
              )}
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground mb-1">MAIN SCRIPT</h4>
                <div className="bg-muted/50 p-4 rounded whitespace-pre-wrap">
                  {previewScript.content}
                </div>
              </div>
              {previewScript.closing && (
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-1">CLOSING</h4>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border-l-4 border-blue-500">
                    {previewScript.closing}
                  </div>
                </div>
              )}
              {Object.keys(previewScript.objection_handlers || {}).length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-2">OBJECTION HANDLERS</h4>
                  <div className="space-y-2">
                    {Object.entries(previewScript.objection_handlers).map(([obj, resp]) => (
                      <div key={obj} className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded border-l-4 border-yellow-500">
                        <p className="font-medium text-sm">If: "{obj}"</p>
                        <p className="text-sm mt-1">{resp}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
            <Button onClick={() => {
              setShowPreview(false);
              openEditDialog(previewScript);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Script
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
